import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  MouseEventHandler,
  PropsWithChildren,
  ReactNode,
  useState,
} from "react";
import { cn } from "../../../utils/classes";
import styles from "./nav-rail.module.css";

export type NavRailProps = PropsWithChildren<{
  /** Logo/icon slot, rendered at the top of the rail — stays pinned at the same position collapsed or expanded. */
  logo?: ReactNode;
  /** Wordmark text shown beside `logo` ONLY when the rail is expanded (hidden entirely while collapsed). */
  logoLabel?: ReactNode;
  /**
   * Bottom-of-rail slot — the identity control / theme toggle typically live
   * here (see `<IdentityControl>` and `<ThemeToggle>`).
   */
  footer?: ReactNode;
  /** Final rail control, rendered below `footer` with its own separator. */
  bottom?: ReactNode;
  /** Controlled expanded state. Omit to let the rail manage it internally. */
  expanded?: boolean;
  /** Initial expanded state when uncontrolled. */
  defaultExpanded?: boolean;
  /** Fires whenever the rail is toggled, controlled or not — persist this. */
  onExpandedChange?: (expanded: boolean) => void;
  /** Whether the built-in collapse/expand affordance is shown. */
  collapsible?: boolean;
  className?: string;
}>;

/**
 * The 64px icon nav rail (`design.md` "sidebar-rail"). Collapsible between a
 * 64px icon-only rail and a wider expanded rail with room for labels next to
 * the identity control. Purely presentational slots — logo, app-icon nav
 * items (`children`, wrap them in `<NavRail.Item>`), and a bottom `footer`.
 */
export function NavRail({
  logo,
  logoLabel,
  footer,
  bottom,
  children,
  expanded: expandedProp,
  defaultExpanded = false,
  onExpandedChange,
  collapsible = true,
  className,
}: NavRailProps) {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const expanded = expandedProp ?? internalExpanded;

  const toggle = () => {
    const next = !expanded;
    if (expandedProp === undefined) {
      setInternalExpanded(next);
    }
    onExpandedChange?.(next);
  };

  return (
    <aside
      className={cn(styles.rail, expanded && styles.expanded, className)}
      data-expanded={expanded}
    >
      {logo && (
        <div className={styles.logo}>
          <span className={styles.logoIconWrap}>{logo}</span>
          {logoLabel && <span className={styles.logoLabel}>{logoLabel}</span>}
        </div>
      )}
      <div className={cn(styles.separator, styles.separatorTop)} />
      {children && <nav className={styles.icons}>{children}</nav>}
      <div className={styles.spacer} />
      {footer && (
        <>
          <div className={styles.separator} />
          <div className={styles.footer}>{footer}</div>
        </>
      )}
      {bottom && (
        <>
          <div className={styles.separator} />
          <div className={styles.bottom}>{bottom}</div>
        </>
      )}
      {collapsible && (
        <button
          type="button"
          className={styles.collapseToggle}
          onClick={toggle}
          aria-expanded={expanded}
          aria-label={expanded ? "Collapse navigation" : "Expand navigation"}
          title={expanded ? "Collapse navigation" : "Expand navigation"}
        >
          {expanded ? <ChevronLeft /> : <ChevronRight />}
        </button>
      )}
    </aside>
  );
}

export type NavRailItemProps = {
  /** Icon element — any pre-sized/styled ReactNode (e.g. a lucide icon). */
  icon: ReactNode;
  /** Accessible name, also used as the collapsed-state tooltip and the
   * collapsed-state visible label. */
  label?: string;
  /**
   * Longer label text shown ONLY once the rail is expanded, in place of
   * `label` (e.g. "QA" collapsed → "QA Automation" expanded). Omit to show
   * `label` in both states. Purely CSS-driven (both spans always render;
   * `.expanded` toggles which is visible) rather than a JS `expanded` prop
   * — `<NavRailItem>` has no other awareness of the rail's collapsed state,
   * and every other collapsed/expanded style difference in this component
   * already works the same way via nav-rail.module.css's own `.expanded`
   * ancestor selector.
   */
  expandedLabel?: string;
  active?: boolean;
  /** Renders as a link instead of a button when provided. */
  href?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  className?: string;
};

export type NavRailSubItemProps = {
  /** Visible and accessible label for the nested destination. */
  label: string;
  /** Optional leading icon, chevron, or story marker. */
  icon?: ReactNode;
  /** Second-level items receive one additional indentation step. */
  depth?: 1 | 2;
  active?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  className?: string;
  ariaExpanded?: boolean;
};

/** A single app-icon nav entry for `<NavRail>`'s icon slot. */
export function NavRailItem({
  icon,
  label,
  expandedLabel,
  active,
  href,
  onClick,
  className,
}: NavRailItemProps) {
  const itemClassName = cn(styles.item, active && styles.active, className);

  const labelContent = expandedLabel ? (
    <>
      <span className={styles.itemLabelCollapsedOnly}>{label}</span>
      <span className={styles.itemLabelExpandedOnly}>{expandedLabel}</span>
    </>
  ) : (
    label
  );

  if (href) {
    return (
      <a
        href={href}
        onClick={onClick as MouseEventHandler<HTMLAnchorElement>}
        className={itemClassName}
        aria-label={label}
        title={label}
      >
        <span className={styles.itemIconBox}>
          <span className={styles.itemIcon}>{icon}</span>
        </span>
        {label && <span className={styles.itemLabel}>{labelContent}</span>}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick as MouseEventHandler<HTMLButtonElement>}
      className={itemClassName}
      aria-label={label}
      title={label}
    >
      <span className={styles.itemIconBox}>
        <span className={styles.itemIcon}>{icon}</span>
      </span>
      {label && <span className={styles.itemLabel}>{labelContent}</span>}
    </button>
  );
}

/**
 * A compact nested destination shown below a `<NavRailItem>` while the rail
 * is expanded. Use depth 1 for component groups and depth 2 for stories.
 */
export function NavRailSubItem({
  label,
  icon,
  depth = 1,
  active,
  onClick,
  className,
  ariaExpanded,
}: NavRailSubItemProps) {
  return (
    <button
      type="button"
      className={cn(
        styles.subItem,
        depth === 2 && styles.subItemDepthTwo,
        active && styles.subItemActive,
        className,
      )}
      onClick={onClick}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      aria-expanded={ariaExpanded}
      title={label}
    >
      {icon && <span className={styles.subItemIcon}>{icon}</span>}
      <span className={styles.subItemLabel}>{label}</span>
    </button>
  );
}

NavRail.Item = NavRailItem;
